import { t as translate } from 'i18next';
import { ExternalLinkIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Button } from '@/components/ui/button';
import { PREVENT_AUTOFILL_PROPS } from '@/components/ui/constants';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { InfobipRcsIntegration, InfobipSmsIntegration } from '@/shared/types/integration';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { trimOnBlur } from '@/util/validation/helpers';

import CardBase from '../Components/CardBase';

export interface ConnectField {
  name: string;
  label: string;
  placeholder: string;
  value: string;
  validate?: yup.StringSchema;
}

interface Props {
  integration: InfobipSmsIntegration | InfobipRcsIntegration;
  title: string;
  docsUrl: string;
  fields: ConnectField[];
}

type ConnectForm = Record<string, string>;

const FORM_ID = 'infobip-connect-form';

const requiredString = yup
  .string()
  .trim()
  .required(() => translate('common.required_field'));

const buildSchema = (fields: ConnectField[]) =>
  yup.object(
    Object.fromEntries(fields.map(({ name, validate }) => [name, validate ?? requiredString]))
  );

export const ConnectCard = ({ integration, title, docsUrl, fields }: Props) => {
  const { t } = useTranslation();
  const { updateIntegration, updateStatus } = useIntegrations(
    integration.desk_id,
    integration.integration_id
  );

  const form = useForm<ConnectForm>({
    mode: 'onTouched',
    resolver: createYupResolver<ConnectForm>(buildSchema(fields)),
    resetOptions: KEEP_DIRTY_VALUES,
    values: Object.fromEntries(fields.map(({ name, value }) => [name, value])),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });

  const onSubmit = (values: ConnectForm) => {
    updateIntegration({ ...integration, config: { ...integration.config, ...values } });
  };

  return (
    <CardBase title={title} titleVariant="secondary">
      <Form {...form}>
        <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
          {fields.map(({ name, label, placeholder }) => (
            <FormField
              key={name}
              control={control}
              name={name}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      {...PREVENT_AUTOFILL_PROPS}
                      onBlur={trimOnBlur(field)}
                      placeholder={placeholder}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          <div className="flex items-center justify-between">
            <Button variant="link-secondary" size="sm" className="gap-1 px-0" asChild>
              <a href={docsUrl} target="_blank" rel="noopener noreferrer">
                {t('common.learn_more')}
                <ExternalLinkIcon className="size-4" />
              </a>
            </Button>
            <Button type="submit" disabled={!isDirty} isLoading={updateStatus === 'pending'}>
              {t('common.connect')}
            </Button>
          </div>
        </form>
      </Form>
    </CardBase>
  );
};
